// A SHAPE'S LINE IS A PHYSICAL SIZE (design system v2, line frames — model
// 1-04 « photo inside a thin line frame »; owner: editor = preview = PDF = 3D).
//
// A `rect` / `ellipse` element stores `strokeWidth` in PDF POINTS — a physical
// size (1 pt = 1/72 inch = 0.3528 mm): themes/expand.ts writes a line frame's
// widthMm × 2.8346, the editor's shape tool writes 1. The three renderers read
// it three ways until now:
//
//   print    pdf-lib borderWidth in points, CENTRED on the box edge, opaque
//   editor   CSS px INSIDE the box, × the element's opacity (0.8 by default),
//            and a gold 1 px border when the shape had no stroke at all
//   preview  mm → cqi inside the box (ThemedPageView)
//
// A 0.5 mm line frame was 1.42 px on screen: 0.7 mm on a 400 px page, 0.3 mm
// on a desktop page — never the 0.5 mm that prints. This file is the one
// answer all three take:
//
//   strokeWidthPt  what the print hands pdf-lib (the stored value — the PDF is
//                  unchanged by this file)
//   strokeMm       the physical width
//   strokeLayerCss the same line on screen: in `cqi` of the page box (it is an
//                  inline-size container 200 mm wide: 1 cqi = 2 mm), on a box
//                  grown by half the line on every side so the line is CENTRED
//                  on the edge like pdf-lib's, opaque like pdf-lib's
//   fillOpacity    the fill's alpha as the print paints it: the colour's own
//                  alpha (#RRGGBBAA) when it has one, else the element opacity
//
// No stroke colour → no line, whatever the width (pdf-lib strokes only with a
// borderColor). Guard: shapePaint.test.ts (with a real PDF).
//
// Pure. No React at run time.

import type { CSSProperties } from 'react';

/** Points per millimetre — the PDF's MM_TO_PT (pdfGenerator.ts) and expand.ts's PT_PER_MM. */
export const PT_PER_MM = 2.8346;
/** The trim page width the page box stands for. 1 cqi = 1 % of it. */
export const PAGE_W_MM = 200;

/** The fill a shape without one gets — the print's defaults (pdfGenerator drawPageElements). */
export const RECT_FILL_DEFAULT = '#00000020';
export const ELLIPSE_FILL_DEFAULT = '#00000015';

export interface ShapeLike {
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  opacity?: number;
}

/** The stored line width, in PDF points — exactly what the print draws (`el.strokeWidth ?? 0`). */
export function strokeWidthPt(el: ShapeLike): number {
  const w = el.strokeWidth;
  return typeof w === 'number' && Number.isFinite(w) && w > 0 ? w : 0;
}

/** Does the print draw a line at all? Only with a colour and a width. */
export function hasStroke(el: ShapeLike): boolean {
  return typeof el.stroke === 'string' && el.stroke.length > 0 && strokeWidthPt(el) > 0;
}

/** The line's physical width in mm (0 when none is drawn). */
export function strokeMm(el: ShapeLike): number {
  return hasStroke(el) ? strokeWidthPt(el) / PT_PER_MM : 0;
}

/** The line's width in `cqi` of a page box that is 200 mm wide. */
export function strokeCqi(el: ShapeLike): number {
  return strokeMm(el) / (PAGE_W_MM / 100);
}

/**
 * The CSS of the line layer, absolutely placed inside the element box: grown
 * by half the line on every side (centred on the edge, as pdf-lib strokes),
 * opaque, never catching the pointer. Null = no line.
 */
export function strokeLayerCss(el: ShapeLike, borderRadius?: CSSProperties['borderRadius']): CSSProperties | null {
  if (!hasStroke(el)) return null;
  const w = strokeCqi(el);
  const out = `${-w / 2}cqi`;
  return {
    position: 'absolute', top: out, right: out, bottom: out, left: out,
    border: `${w}cqi solid ${el.stroke}`, boxSizing: 'border-box', pointerEvents: 'none',
    ...(borderRadius != null ? { borderRadius } : {}),
  };
}

/** The alpha a #RRGGBBAA colour carries, or undefined (the print's parseAlphaFromHex). */
export function hexAlpha(hex: string | undefined): number | undefined {
  if (!hex) return undefined;
  const h = hex.replace('#', '');
  if (h.length !== 8) return undefined;
  const a = parseInt(h.slice(6, 8), 16);
  return Number.isNaN(a) ? undefined : a / 255;
}

/**
 * How opaque the FILL layer is drawn on screen so it matches the print: a
 * colour with its own alpha already carries it (CSS reads #RRGGBBAA) — the
 * element opacity is then ignored, as in the PDF; otherwise the element
 * opacity (1 when unset, the PDF's default).
 */
export function fillOpacity(el: ShapeLike, fallbackFill: string): number {
  return hexAlpha(el.fill ?? fallbackFill) != null ? 1 : (el.opacity ?? 1);
}
